import {
  DocumentClassifier,
  type ListingApplicationDocumentInput,
} from "@/graphql/generated/operations";
import type { IngestedFile, IngestResult } from "@/lib/file-ingest";
import { MAX_APPLICATION_DOCUMENTS, MAX_APPLICATION_TOTAL_BYTES } from "@/lib/upload-limits";

// Die Dokumente einer Bewerbung, bevor sie abgeschickt werden: ein Platz für den Lebenslauf, daneben
// weitere Dokumente. Alle sind freiwillig; nur ein Lebenslauf wird als solcher markiert, damit genesis
// daraus das Profil vorbefüllt. Alles geht in EINER Mutation an genesis und landet dort in der Organisationsakte
// (@external-adr GENESIS-0087-application-phases-are-separate-from-decisions (GENESIS-0071 §2)).
//
// Die Grenzen prüft der Client vorab, damit eine Bewerbung nicht erst nach dem Hochladen scheitert:
// genesis nimmt höchstens fünf Dateien zu je 10 MiB, der Transport trägt zusammen nur rund 11 MB
// (upload-limits.ts). Der Platz des Lebenslaufs zählt immer mit, auch solange er leer bleibt; sonst
// könnten weitere Dokumente ihn verdrängen.

export interface ApplicationDocuments {
  cv: IngestedFile | null;
  others: IngestedFile[];
}

export type DocumentSlotKind = "cv" | "others";

export type ApplicationDocumentProblem =
  | { kind: "tooLarge"; name: string }
  | { kind: "unsupportedType"; name: string }
  | { kind: "empty"; name: string }
  | { kind: "tooMany"; name: string }
  | { kind: "totalTooLarge"; name: string };

export const NO_APPLICATION_DOCUMENTS: ApplicationDocuments = { cv: null, others: [] };

/** Wie viele weitere Dokumente neben dem Lebenslauf Platz haben. */
export const MAX_OTHER_APPLICATION_DOCUMENTS = MAX_APPLICATION_DOCUMENTS - 1;

export function totalBytes(documents: ApplicationDocuments): number {
  return (documents.cv?.size ?? 0) + documents.others.reduce((sum, file) => sum + file.size, 0);
}

/**
 * Nimmt gelesene Dateien in einen Platz auf und sagt, was nicht passte. Der Lebenslauf-Platz hält
 * eine Datei; eine neue ersetzt die alte. Was über die Anzahl oder die Gesamtgröße hinausginge, bleibt
 * draußen, in der Reihenfolge der Auswahl.
 */
export function admitApplicationDocuments(
  current: ApplicationDocuments,
  slot: DocumentSlotKind,
  ingested: IngestResult,
  totalLimit: number = MAX_APPLICATION_TOTAL_BYTES,
): { next: ApplicationDocuments; problems: ApplicationDocumentProblem[] } {
  const problems: ApplicationDocumentProblem[] = ingested.rejected.map(({ file, reason }) => ({
    kind: reason,
    name: file.name,
  }));
  let next: ApplicationDocuments = slot === "cv" ? { ...current, cv: null } : current;
  for (const file of ingested.accepted) {
    if (file.size === 0) {
      problems.push({ kind: "empty", name: file.name });
      continue;
    }
    const full = slot === "cv" ? next.cv !== null : next.others.length >= MAX_OTHER_APPLICATION_DOCUMENTS;
    if (full) {
      problems.push({ kind: "tooMany", name: file.name });
      continue;
    }
    if (totalBytes(next) + file.size > totalLimit) {
      problems.push({ kind: "totalTooLarge", name: file.name });
      continue;
    }
    next = slot === "cv" ? { ...next, cv: file } : { ...next, others: [...next.others, file] };
  }
  // Ein abgelehnter Ersatz lässt den bisherigen Lebenslauf stehen.
  if (slot === "cv" && next.cv === null) next = { ...next, cv: current.cv };
  return { next, problems };
}

/**
 * Was an die öffentliche Einreichung geht: dieselbe Reihenfolge, der Inhalt als base64-Text. Bei einer
 * Unternehmensbewerbung ist nichts ein Lebenslauf; genesis verwirft den Klassifikator dort ohnehin, und kein Unternehmensprofil
 * befüllt das Profil der Kontaktperson (@external-adr GENESIS-0083-a-company-applies-without-an-account-through-its-proven-contact).
 */
export function publicApplicationDocumentsInput(
  documents: ApplicationDocuments,
  company: boolean,
): { fileName: string; content: string; classifier?: "CURRICULUM_VITAE" }[] {
  const input = (file: IngestedFile) => ({ fileName: file.name, content: file.content });
  return [
    ...(documents.cv ? [company ? input(documents.cv) : { ...input(documents.cv), classifier: "CURRICULUM_VITAE" as const }] : []),
    ...documents.others.map(input),
  ];
}

/** Was an `applyToListing` geht: der Lebenslauf zuerst und als solcher markiert, Titel = Dateiname. */
export function applicationDocumentsInput(documents: ApplicationDocuments): ListingApplicationDocumentInput[] {
  const input = (file: IngestedFile): ListingApplicationDocumentInput => ({
    fileName: file.name,
    content: { content: file.content, mimeType: file.mimeType },
  });
  return [
    ...(documents.cv ? [{ ...input(documents.cv), classifier: DocumentClassifier.CurriculumVitae }] : []),
    ...documents.others.map(input),
  ];
}
